/**
 * ArchiveControls — Iter 51.1 (2026-05-18)
 *
 * Small reusable bits for the soft-delete (Archive) pattern shared
 * across Clients / Proposals / Purchase Orders / Leads / AMC schedules:
 *   - <ShowArchivedToggle />        : header checkbox that flips the
 *                                     `include_archived` query param
 *   - <ArchivedBadge />             : muted "ARCHIVED" pill on archived rows
 *   - <ArchiveRowButton />          : per-row Archive action (super-admin)
 *   - <UnarchiveRowButton />        : per-row Unarchive action (super-admin,
 *                                     visible only when row is archived)
 *   - useArchiveAction()            : confirm-dialog state + handlers
 *
 * Tightly scoped: no styling assumptions beyond Tailwind utility classes
 * the rest of the app already uses; no business logic beyond the API call.
 */
import { useState } from "react";
import { Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";


export function ShowArchivedToggle({ value, onChange, count = null, testid = "show-archived-toggle" }) {
    return (
        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none" data-testid={testid}>
            <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300"
            />
            Show archived
            {count != null && count > 0 && (
                <span className="font-mono text-slate-400">({count})</span>
            )}
        </label>
    );
}


export function ArchivedBadge({ testid }) {
    return (
        <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-slate-200 text-slate-600"
            data-testid={testid}
            title="This record has been archived (soft-deleted). It's hidden from operational lists by default."
        >
            <Archive size={9} weight="fill" /> Archived
        </span>
    );
}


export function ArchiveRowButton({ onClick, testid, size = "sm" }) {
    return (
        <Button
            size={size}
            variant="ghost"
            onClick={onClick}
            className="gap-1 text-rose-700 hover:text-rose-900"
            data-testid={testid}
            title="Archive (soft-delete) — hides from operational views; reversible by super-admin"
        >
            <Archive size={13} /> Archive
        </Button>
    );
}


export function UnarchiveRowButton({ onClick, testid, size = "sm" }) {
    return (
        <Button
            size={size}
            variant="ghost"
            onClick={onClick}
            className="gap-1 text-emerald-700 hover:text-emerald-900"
            data-testid={testid}
            title="Restore this archived row to operational views"
        >
            <ArrowCounterClockwise size={13} /> Unarchive
        </Button>
    );
}


/**
 * Reusable confirm + execute hook for archive/unarchive.
 *
 * @param entityType String label used in the confirm dialog title ("Client", "Proposal", …)
 * @param archiveUrl Fn(row) → API path for the archive POST
 * @param unarchiveUrl Fn(row) → API path for the unarchive POST
 * @param onDone Callback after a successful archive/unarchive (typically reload())
 *
 * Returns: { pending, openArchive, openUnarchive, dialog }
 *   - openArchive(row): show the archive confirm modal for this row
 *   - openUnarchive(row): show the unarchive confirm modal for this row
 *   - dialog: JSX to render once at the page bottom
 *   - pending: bool — true while the API call is in flight
 */
export function useArchiveAction({ entityType, archiveUrl, unarchiveUrl, onDone, describe }) {
    const [target, setTarget] = useState(null); // { row, mode: 'archive'|'unarchive' }
    const [pending, setPending] = useState(false);

    const openArchive = (row) => setTarget({ row, mode: "archive" });
    const openUnarchive = (row) => setTarget({ row, mode: "unarchive" });

    const close = () => !pending && setTarget(null);

    const confirm = async () => {
        if (!target) return;
        setPending(true);
        try {
            const url = target.mode === "archive"
                ? archiveUrl(target.row)
                : unarchiveUrl(target.row);
            await api.post(url);
            toast.success(
                target.mode === "archive"
                    ? `Archived ${entityType.toLowerCase()}`
                    : `${entityType} restored`
            );
            setTarget(null);
            if (onDone) await onDone();
        } catch (err) {
            toast.error(formatApiError(
                err,
                target.mode === "archive" ? "Archive failed" : "Unarchive failed",
            ));
        } finally {
            setPending(false);
        }
    };

    const isArchive = target?.mode === "archive";
    const Icon = isArchive ? Archive : ArrowCounterClockwise;
    const accent = isArchive ? "text-rose-700" : "text-emerald-700";
    const btnClass = isArchive
        ? "bg-rose-600 hover:bg-rose-700 text-white"
        : "bg-emerald-600 hover:bg-emerald-700 text-white";

    const dialog = (
        <AlertDialog open={!!target} onOpenChange={(v) => !v && close()}>
            <AlertDialogContent data-testid={`${entityType.toLowerCase()}-archive-confirm-dialog`}>
                <AlertDialogHeader>
                    <AlertDialogTitle className={`flex items-center gap-2 ${accent}`}>
                        <Icon size={20} weight="duotone" />{" "}
                        {isArchive ? `Archive this ${entityType.toLowerCase()}?` : `Restore this ${entityType.toLowerCase()}?`}
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                            {target?.row && describe && (
                                <div className="reedos-card bg-slate-50 p-3 text-[12px]">
                                    {describe(target.row)}
                                </div>
                            )}
                            {isArchive ? (
                                <>
                                    <div>
                                        The {entityType.toLowerCase()} will be{" "}
                                        <strong>hidden from operational views</strong>{" "}
                                        and edits will be blocked.
                                    </div>
                                    <div className="text-emerald-700">
                                        ✓ Historical records linked to this row are{" "}
                                        <strong>preserved verbatim</strong>. No cascade delete.
                                    </div>
                                    <div className="text-[11px] text-slate-500 italic">
                                        Reversible — a super-admin can restore it later via "Show archived".
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        The {entityType.toLowerCase()} will be{" "}
                                        <strong>restored to active operational views</strong>{" "}
                                        and become editable again.
                                    </div>
                                    <div className="text-[11px] text-slate-500 italic">
                                        Audit logged. No data is duplicated — the same row is unhidden.
                                    </div>
                                </>
                            )}
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        data-testid={`${entityType.toLowerCase()}-archive-cancel`}
                        disabled={pending}
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={confirm}
                        className={btnClass}
                        data-testid={`${entityType.toLowerCase()}-archive-confirm`}
                        disabled={pending}
                    >
                        {pending
                            ? (isArchive ? "Archiving…" : "Restoring…")
                            : (isArchive ? "Yes, archive" : "Yes, restore")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return { pending, openArchive, openUnarchive, dialog };
}
